/**
 * 请求拦截器
 * - authReqHeaderInterceptor: 白名单 URL 不注入 Token，其余注入 Authorization header
 * - authResponseErrorInterceptor: 401 时尝试刷新 Token，刷新失败才跳登录页
 * - startTokenRefreshTimer / stopTokenRefreshTimer: 定时检测 Token 过期并主动刷新
 */

import type { RequestOptions } from '@@/plugin-request/request';
import {
  getToken,
  getTokenInfo,
  isCookieMode,
  removeToken,
  setTokenInfo,
} from './token';

const loginPath = '/user/login';

/** Token 刷新 API 路径 */
const REFRESH_URL = '/api/v1/authn/me/auth/token/refresh';

/** 不需要注入 Token 的 URL 白名单 */
const AUTH_WHITELIST = [
  '/api/v1/authn/public/auth/captcha',
  '/api/v1/authn/public/auth/login',
  '/api/v1/authn/public/auth/register',
];

/** 距过期多少秒时主动刷新（5 分钟） */
const REFRESH_THRESHOLD_SEC = 5 * 60;
/** 定时检查间隔（60 秒） */
const CHECK_INTERVAL_MS = 60 * 1000;

// ==================== 刷新锁 ====================
// 防止多个并发 401 同时触发多次刷新
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshRejectCallbacks: Array<(error: Error) => void> = [];

function onTokenRefreshed(newToken: string) {
  for (const cb of refreshSubscribers) {
    cb(newToken);
  }
  refreshSubscribers = [];
  refreshRejectCallbacks = [];
}

function onTokenRefreshFailed(error: Error) {
  for (const cb of refreshRejectCallbacks) {
    cb(error);
  }
  refreshSubscribers = [];
  refreshRejectCallbacks = [];
}

function addRefreshSubscriber(
  cb: (token: string) => void,
  onError: (error: Error) => void,
) {
  refreshSubscribers.push(cb);
  refreshRejectCallbacks.push(onError);
}

/**
 * 调用后端刷新 Token 接口（直接用 fetch，不走 umi request 避免拦截器循环）
 */
async function doRefreshToken(
  currentToken: string,
): Promise<{ token: string; expires_at: number } | null> {
  try {
    const resp = await fetch(REFRESH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentToken}`,
      },
      body: '{}',
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    if (json?.status === 'ok' && json?.data?.token) {
      return { token: json.data.token, expires_at: json.data.expires_at };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * 尝试刷新 Token 并更新 localStorage
 * 返回新 token 或 null（刷新失败）
 */
async function tryRefreshToken(): Promise<string | null> {
  const currentToken = getToken();
  if (!currentToken) return null;

  const result = await doRefreshToken(currentToken);
  if (!result) return null;

  // 更新 localStorage 中的 TokenInfo
  const info = getTokenInfo();
  if (info) {
    setTokenInfo({
      ...info,
      token: result.token,
      expires_at: result.expires_at,
    });
  }
  return result.token;
}

// ==================== 定时主动刷新 ====================
let refreshTimerId: ReturnType<typeof setInterval> | null = null;

/**
 * 启动 Token 定时刷新检查
 * 每 60 秒检查一次，如果 Token 将在 5 分钟内过期则主动刷新
 * Cookie 模式下不启动（由服务端控制过期）
 */
export function startTokenRefreshTimer() {
  // Cookie 模式下不启动定时刷新
  if (isCookieMode()) {
    return;
  }
  stopTokenRefreshTimer();
  refreshTimerId = setInterval(async () => {
    const info = getTokenInfo();
    if (!info) return;
    const nowSec = Math.floor(Date.now() / 1000);
    const remainingSec = info.expires_at - nowSec;
    // Token 将在阈值内过期，主动刷新
    if (remainingSec > 0 && remainingSec <= REFRESH_THRESHOLD_SEC) {
      console.log('[TokenRefresh] Token 即将过期，主动刷新...');
      const newToken = await tryRefreshToken();
      if (newToken) {
        console.log('[TokenRefresh] 主动刷新成功');
      } else {
        console.warn('[TokenRefresh] 主动刷新失败');
      }
    }
  }, CHECK_INTERVAL_MS);
}

/**
 * 停止 Token 定时刷新检查
 */
export function stopTokenRefreshTimer() {
  if (refreshTimerId !== null) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
}

// ==================== 拦截器 ====================

/**
 * 判断 URL 是否在白名单中
 */
export function isWhitelistUrl(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_WHITELIST.some((w) => url.includes(w));
}

/**
 * 请求拦截器：注入 Authorization Bearer Token
 * 白名单 URL 不注入
 * Cookie 模式下不注入（由浏览器自动携带）
 */
export const authReqHeaderInterceptor = (
  config: RequestOptions,
): RequestOptions => {
  if (isWhitelistUrl(config.url)) {
    return config;
  }

  // Cookie 模式下不注入 Authorization header
  if (isCookieMode()) {
    return config;
  }

  const token = getToken();
  if (token) {
    const headers: Record<string, string> = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };

    // 多租户支持：注入租户上下文头 (Requirement 12.2)
    const tokenInfo = getTokenInfo();
    if (tokenInfo?.tenant_id) {
      headers['X-Tenant-ID'] = tokenInfo.tenant_id;
    }
    if (tokenInfo?.app_id) {
      headers['X-App-ID'] = tokenInfo.app_id;
    }

    return { ...config, headers };
  }

  return config;
};

/**
 * 响应拦截器（成功）：直接透传
 */
export const authResponseInterceptor = (response: any): any => {
  return response;
};

/**
 * 响应拦截器（错误）：处理 401
 * Cookie 模式：直接跳转登录页（不刷新 token）
 * localStorage 模式：
 * 1. 收到 401 时先尝试刷新 Token
 * 2. 刷新成功 → 用新 Token 重试原请求
 * 3. 刷新失败 → 清除 Token 并跳转登录页
 * 4. 多个并发 401 只触发一次刷新，其余排队等待
 */
export const authResponseErrorInterceptor = (error: any) => {
  const status = error?.response?.status;
  const requestUrl = error?.config?.url || '';

  // 非 401 或者是刷新接口本身的 401，直接放行
  if (status !== 401 || requestUrl.includes(REFRESH_URL)) {
    return Promise.reject(error);
  }

  // Cookie 模式：直接跳转登录页（不刷新 token）
  if (isCookieMode()) {
    removeToken();
    const { pathname } = window.location;
    if (pathname !== loginPath) {
      window.location.href = loginPath;
    }
    return Promise.reject(error);
  }

  // localStorage 模式：尝试刷新 Token
  if (!isRefreshing) {
    isRefreshing = true;
    tryRefreshToken()
      .then((newToken) => {
        if (newToken) {
          onTokenRefreshed(newToken);
        } else {
          // 刷新失败，跳登录页
          removeToken();
          stopTokenRefreshTimer();
          // 通知所有等待的请求刷新失败
          onTokenRefreshFailed(new Error('Token refresh failed'));
          const { pathname } = window.location;
          if (pathname !== loginPath) {
            window.location.href = loginPath;
          }
        }
      })
      .finally(() => {
        isRefreshing = false;
      });
  }

  // 将当前失败的请求加入队列，等刷新完成后用新 Token 重试
  const originalConfig = error.config;
  return new Promise((resolve, reject) => {
    addRefreshSubscriber(
      (newToken: string) => {
        try {
          if (originalConfig?.headers) {
            originalConfig.headers.Authorization = `Bearer ${newToken}`;
          }
          // 标记已重试，避免无限循环
          originalConfig._retried = true;
          // 通过 XMLHttpRequest/fetch 重新发起请求
          const { url, method, data, headers } = originalConfig;
          const fetchHeaders: Record<string, string> = {};
          if (headers) {
            // axios headers 可能是 AxiosHeaders 对象，需要提取
            for (const key of Object.keys(headers)) {
              const val =
                typeof headers.get === 'function'
                  ? headers.get(key)
                  : headers[key];
              if (val && typeof val === 'string') fetchHeaders[key] = val;
            }
          }
          fetchHeaders.Authorization = `Bearer ${newToken}`;
          const fetchOpts: RequestInit = {
            method: (method || 'GET').toUpperCase(),
            headers: fetchHeaders,
          };
          if (data && !['GET', 'HEAD'].includes(fetchOpts.method ?? 'GET')) {
            fetchOpts.body =
              typeof data === 'string' ? data : JSON.stringify(data);
          }
          fetch(url, fetchOpts)
            .then(async (resp) => {
              const json = await resp.json();
              // 构造类似 axios response 的结构
              resolve({
                data: json,
                status: resp.status,
                headers: resp.headers,
              });
            })
            .catch(reject);
        } catch (retryError) {
          reject(retryError);
        }
      },
      (refreshError: Error) => {
        // 刷新失败时，拒绝原请求
        reject(refreshError);
      },
    );
  });
};
